/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSetSort';

const ActionSetSortManager: ActionManagerFn<'ActionSetSort'> = (
  platform: Platform,
): ActionManager<'ActionSetSort'> => {
  return {
    ...baseActionManager(platform, 'ActionSetSort'),
    defaultValue,
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (
        parent.type === 'ActionQuery' || parent.type === 'TriggerBeforeQuery'
      );
    },
    props: {
      ...baseActionProps,
      attributeCode: {
        label: 'Attribute',
        type: 'ViewAttributeChooser',
        required: true,
        aliasType: 'QUERY_FILTER',
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
      },
      sortOrder: {
        label: 'Sort Order',
        type: 'select',
        options: { ASC: 'Ascending', DESC: 'Descending' },
        required: true,
      },
    },
    name: 'Set Sort',
    getDisplayText: (item) => item.props.attributeCode,
    description: `**Overview**<br/>
The Set Sort action is a query structuring component designed to define the order in which records are returned from the database. It corresponds directly to the ORDER BY clause in SQL. By adding this component to a query configuration, developers ensure that data arrives at the application already organized according to specific business rules, rather than in an arbitrary insertion order.<br/><br/>
This component cannot exist independently; it is strictly an architectural element used within ActionQuery or TriggerBeforeQuery. While the application's UI grids often allow users to click headers to sort data on the client side, this action sets the default server-side sort order, which is critical for performance and initial presentation, especially when dealing with paginated data.

**Key Features**<br/>
* **Server-Side Sorting:** Instructs the database engine to perform the sorting before sending the data, which is far more efficient than fetching thousands of unsorted records and trying to organize them in the browser.
* **Directional Control:** Explicitly supports both Ascending (A-Z, 0-9, Oldest-Newest) and Descending (Z-A, 9-0, Newest-Oldest) sort orders via the sortOrder property.
* **Strict Parent Scope:** Enforces usage validity via isAllowedInsideParent, ensuring it only functions within valid data querying contexts (ActionQuery, TriggerBeforeQuery) where the result set structure is mutable.

**Properties**<br/>
* **Attribute (attributeCode) (Required):** A ViewAttributeChooser property.
  * *Function:* The specific field (column) to use as the basis for sorting.
* **Sort Order (Required):** A selection property.
  * *Options:* Ascending (ASC) or Descending (DESC).
  * *Function:* Defines the direction of the sort.

**Use Cases**<br/>
* **Chronological Feeds:** Sorting a "News Feed" or "Audit Log" by CreatedDate in Descending order so the most recent events always appear at the top.
* **Alphabetical Lists:** Sorting a "Customer Directory" by LastName in Ascending order for easier browsing.
* **Ranked Data:** Sorting a "Leaderboard" by Score in Descending order to show the top performers first.`,
  };
};

export default ActionSetSortManager;
